import { ColorInput } from "../inputs/ColorInput";
import { DimensionInput } from "../inputs/DimensionInput";
import { FontSizeInput } from "../inputs/FontSizeInput";
import { IconInput } from "../inputs/IconInput";
import { ImageInput } from "../inputs/ImageInput";
import { NumberInput } from "../inputs/NumberInput";
import { PixelInput } from "../inputs/PixelInput";
import { RichTextInput } from "../inputs/RichTextInput";
import { SelectInput, SelectOption } from "../inputs/SelectInput";
import { TextInput } from "../inputs/TextInput";

import type { AttrPrefix, ParsedAttr } from "./parseDefinition";
import { attrLabel } from "./parseDefinition";

const selectOptions: Partial<Record<AttrPrefix, SelectOption[]>> = {
  // Typography
  align: [
    { value: "left", label: "Left" },
    { value: "center", label: "Center" },
    { value: "right", label: "Right" },
    { value: "justify", label: "Justify" },
  ],
  fontstyle: [
    { value: "normal", label: "Normal" },
    { value: "italic", label: "Italic" },
  ],
  fontweight: [
    { value: "300", label: "Light" },
    { value: "400", label: "Regular" },
    { value: "500", label: "Medium" },
    { value: "600", label: "Semi-bold" },
    { value: "700", label: "Bold" },
  ],
  textdecoration: [
    { value: "none", label: "None" },
    { value: "underline", label: "Underline" },
    { value: "line-through", label: "Line-through" },
    { value: "overline", label: "Overline" },
  ],
  textoverflow: [
    { value: "clip", label: "Clip" },
    { value: "ellipsis", label: "Ellipsis" },
  ],
  texttransform: [
    { value: "none", label: "None" },
    { value: "uppercase", label: "Uppercase" },
    { value: "lowercase", label: "Lowercase" },
    { value: "capitalize", label: "Capitalize" },
  ],
  whitespace: [
    { value: "normal", label: "Normal" },
    { value: "nowrap", label: "No wrap" },
    { value: "pre", label: "Pre" },
    { value: "pre-wrap", label: "Pre-wrap" },
    { value: "pre-line", label: "Pre-line" },
  ],

  // Layout
  alignitems: [
    { value: "flex-start", label: "Start" },
    { value: "flex-end", label: "End" },
    { value: "center", label: "Center" },
    { value: "stretch", label: "Stretch" },
    { value: "baseline", label: "Baseline" },
  ],
  display: [
    { value: "block", label: "Block" },
    { value: "flex", label: "Flex" },
    { value: "inline", label: "Inline" },
    { value: "inline-block", label: "Inline-block" },
    { value: "inline-flex", label: "Inline-flex" },
    { value: "grid", label: "Grid" },
    { value: "none", label: "None" },
  ],
  flexdirection: [
    { value: "row", label: "Row" },
    { value: "row-reverse", label: "Row reverse" },
    { value: "column", label: "Column" },
    { value: "column-reverse", label: "Column reverse" },
  ],
  flexwrap: [
    { value: "nowrap", label: "No wrap" },
    { value: "wrap", label: "Wrap" },
    { value: "wrap-reverse", label: "Wrap reverse" },
  ],
  justifycontent: [
    { value: "flex-start", label: "Start" },
    { value: "flex-end", label: "End" },
    { value: "center", label: "Center" },
    { value: "space-between", label: "Space between" },
    { value: "space-around", label: "Space around" },
    { value: "space-evenly", label: "Space evenly" },
  ],
  objectfit: [
    { value: "contain", label: "Contain" },
    { value: "cover", label: "Cover" },
    { value: "fill", label: "Fill" },
    { value: "none", label: "None" },
    { value: "scale-down", label: "Scale down" },
  ],
  objectposition: [
    { value: "center", label: "Center" },
    { value: "top", label: "Top" },
    { value: "bottom", label: "Bottom" },
    { value: "left", label: "Left" },
    { value: "right", label: "Right" },
    { value: "top left", label: "Top left" },
    { value: "top right", label: "Top right" },
    { value: "bottom left", label: "Bottom left" },
    { value: "bottom right", label: "Bottom right" },
  ],
  position: [
    { value: "static", label: "Static" },
    { value: "relative", label: "Relative" },
    { value: "absolute", label: "Absolute" },
    { value: "fixed", label: "Fixed" },
    { value: "sticky", label: "Sticky" },
  ],

  // Border / Box
  borderstyle: [
    { value: "none", label: "None" },
    { value: "solid", label: "Solid" },
    { value: "dashed", label: "Dashed" },
    { value: "dotted", label: "Dotted" },
    { value: "double", label: "Double" },
  ],
  overflow: [
    { value: "visible", label: "Visible" },
    { value: "hidden", label: "Hidden" },
    { value: "scroll", label: "Scroll" },
    { value: "auto", label: "Auto" },
  ],
  shadow: [
    { value: "none", label: "None" },
    { value: "0 1px 2px rgba(0,0,0,0.05)", label: "Small" },
    { value: "0 4px 6px rgba(0,0,0,0.1)", label: "Medium" },
    { value: "0 10px 15px rgba(0,0,0,0.1)", label: "Large" },
    { value: "0 20px 25px rgba(0,0,0,0.15)", label: "X-Large" },
  ],

  // Interaction / Visual
  cursor: [
    { value: "default", label: "Default" },
    { value: "pointer", label: "Pointer" },
    { value: "text", label: "Text" },
    { value: "move", label: "Move" },
    { value: "not-allowed", label: "Not allowed" },
    { value: "grab", label: "Grab" },
  ],
  pointerevents: [
    { value: "auto", label: "Auto" },
    { value: "none", label: "None" },
  ],
  visibility: [
    { value: "visible", label: "Visible" },
    { value: "hidden", label: "Hidden" },
  ],
};

interface ConfigFieldProps {
  attr: ParsedAttr;
  onChange: (value: string) => void;
}

export const ConfigField = ({ attr, onChange }: ConfigFieldProps) => {
  const label = attrLabel(attr.name);
  const props = { label, value: attr.value, onChange };

  // Select-based prefixes
  const options = attr.prefix ? selectOptions[attr.prefix] : undefined;
  if (options) {
    return <SelectInput {...props} options={options} />;
  }

  switch (attr.prefix) {
    case "color":
      return <ColorInput {...props} />;
    case "dimension":
      return <DimensionInput {...props} />;
    case "fontsize":
      return <FontSizeInput {...props} />;
    case "number":
      return <NumberInput {...props} />;
    case "pixel":
      return <PixelInput {...props} />;
    case "rich":
      return <RichTextInput {...props} />;
    case "image":
      return <ImageInput {...props} />;
    case "icon":
      return <IconInput {...props} />;
    default:
      return <TextInput {...props} />;
  }
};
